/* eslint-disable react/prop-types */
import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import dayjs from 'dayjs';

const gridWrapSx = {
	width: '100%',
	border: '1px solid #e3ecf7',
	borderRadius: '8px',
	overflow: 'auto',
	backgroundColor: '#fff',
	maxHeight: 452,
	boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
};

const tableSx = {
	width: '100%',
	minWidth: 980,
	borderCollapse: 'separate',
	borderSpacing: 0,
	fontFamily: 'Poppins',
	'& th': {
		position: 'sticky',
		top: 0,
		zIndex: 1,
		backgroundColor: '#f7f9fc',
		color: '#172d58',
		fontSize: '12px',
		fontWeight: 700,
		textAlign: 'left',
		padding: '10px 12px',
		borderBottom: '1px solid #e3ecf7',
		whiteSpace: 'nowrap',
	},
	'& td': {
		color: '#172d58',
		fontSize: '12px',
		fontWeight: 500,
		lineHeight: 1.45,
		padding: '10px 12px',
		borderBottom: '1px solid #edf2f8',
		verticalAlign: 'top',
		backgroundColor: '#fff',
		maxWidth: 280,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	'& tbody tr:hover td': {
		backgroundColor: '#f8fbff',
	},
	'& tbody tr:last-child td': {
		borderBottom: 0,
	},
};

const emptySx = {
	height: '100%',
	minHeight: 220,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	textAlign: 'center',
	color: '#60739d',
	fontFamily: 'Poppins',
	fontSize: '14px',
	fontWeight: 600,
};

const formatHeader = (key) =>
	key
		.replace(/_/g, ' ')
		.replace(/([a-z])([A-Z])/g, '$1 $2')
		.replace(/\b\w/g, (char) => char.toUpperCase());

const formatValue = (value) => {
	if (value === null || value === undefined || value === '') return '--';
	if (typeof value === 'boolean') return value ? 'Yes' : 'No';
	if (typeof value === 'object') {
		if (value.Name) return value.Name;
		if (value.Fname || value.Lname) {
			return [value.Lname, value.Fname].filter(Boolean).join(', ');
		}
		return JSON.stringify(value);
	}
	if (
		typeof value === 'string' &&
		/^\d{4}-\d{2}-\d{2}/.test(value) &&
		dayjs(value).isValid()
	) {
		return dayjs(value).format('MM/DD/YYYY');
	}
	return value;
};

function ReportResultsGrid({
	rows = [],
	emptyMessage = 'Please apply filter to get reports',
	loading = false,
}) {
	const columns = React.useMemo(() => {
		const keys = [];
		rows.forEach((row) => {
			Object.keys(row || {}).forEach((key) => {
				if (!keys.includes(key)) keys.push(key);
			});
		});
		return keys.filter((key) => key !== 'id');
	}, [rows]);

	if (loading) {
		return (
			<Box sx={emptySx}>
				<CircularProgress size={28} sx={{ color: '#2E68BA' }} />
			</Box>
		);
	}

	if (!rows?.length) {
		return (
			<Box sx={emptySx}>
				<Typography sx={{ fontFamily: 'Poppins', fontWeight: 600 }}>
					{emptyMessage}
				</Typography>
			</Box>
		);
	}

	return (
		<Box sx={gridWrapSx}>
			<Box component="table" sx={tableSx}>
				<thead>
					<tr>
						{columns.map((column) => (
							<th key={column}>{formatHeader(column)}</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map((row, rowIndex) => (
						<tr key={row?.id || row?.patient_id || row?.Visit_id || rowIndex}>
							{columns.map((column) => (
								<td key={column}>{formatValue(row?.[column])}</td>
							))}
						</tr>
					))}
				</tbody>
			</Box>
		</Box>
	);
}

export default ReportResultsGrid;
